// src/api/offlineCacheInterceptor.ts
// Standalone module for offline-aware API interception.
// Caches successful responses to IndexedDB and serves from cache on network errors.
// Mutations are queued in the sync queue when offline.

import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { db, StoreName, SyncOperation } from '../services/offlineDb';

// ── Online-Only Route Patterns ──
// These routes require real-time customer↔restaurant interaction and NEVER work offline.

const ONLINE_ONLY_PATTERNS: RegExp[] = [
  /^\/orders\/?$/,                        // POST /orders (customer placing)
  /^\/orders\/track\//,                   // GET /orders/track/:num
  /^\/orders\/[^/]+\/cancel$/,            // POST /orders/:id/cancel (customer)
  /^\/cart/,                              // All /cart/* routes
  /^\/tables\/session\/start$/,           // GET /tables/session/start
  /^\/orders\/restaurant\/stream$/,       // SSE stream
];

export function isOnlineOnlyRoute(url: string): boolean {
  // Normalize: strip the /api/v1 prefix if present
  const cleanUrl = url.replace(/^\/api\/v\d+/, '');
  return ONLINE_ONLY_PATTERNS.some((pattern) => pattern.test(cleanUrl));
}

// ── Cache Key Strategy ──
// Maps API URLs to IndexedDB store names and keys

interface CacheMapping {
  storeName: StoreName;
  mode: 'single' | 'list';  // 'single' = one record, 'list' = array of records
  extractKey?: (url: string) => string | null;
}

const CACHE_MAPPINGS: Array<{ pattern: RegExp; mapping: CacheMapping }> = [
  // Delivery Company Orders
  { pattern: /^\/delivery-companies\/orders\/?(\?.*)?$/, mapping: { storeName: 'deliveryCompanyOrders', mode: 'list' } },
  { pattern: /^\/delivery-companies\/orders\/([^/?]+)$/, mapping: { storeName: 'deliveryCompanyOrders', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Delivery Companies
  { pattern: /^\/delivery-companies\/?(\?.*)?$/, mapping: { storeName: 'deliveryCompanies', mode: 'list' } },
  { pattern: /^\/delivery-companies\/([^/?]+)$/, mapping: { storeName: 'deliveryCompanies', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Categories
  { pattern: /^\/categories\/?(\?.*)?$/, mapping: { storeName: 'categories', mode: 'list' } },
  { pattern: /^\/categories\/([^/?]+)$/, mapping: { storeName: 'categories', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Dishes
  { pattern: /^\/dishes\/?(\?.*)?$/, mapping: { storeName: 'dishes', mode: 'list' } },
  { pattern: /^\/dishes\/([^/?]+)$/, mapping: { storeName: 'dishes', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Orders (restaurant side)
  { pattern: /^\/orders\/restaurant\/dashboard-statistics\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'dashboardStatistics' } },
  { pattern: /^\/orders\/restaurant\/statistics\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'orderStats' } },
  { pattern: /^\/orders\/restaurant\/all\/?(\?.*)?$/, mapping: { storeName: 'orders', mode: 'list' } },
  { pattern: /^\/orders\/restaurant\/([^/?]+)$/, mapping: { storeName: 'orders', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },
  { pattern: /^\/orders\/restaurant\/?(\?.*)?$/, mapping: { storeName: 'orders', mode: 'list' } },
  { pattern: /^\/orders\/([^/?]+)$/, mapping: { storeName: 'orders', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Tables
  { pattern: /^\/tables\/?(\?.*)?$/, mapping: { storeName: 'tables', mode: 'list' } },

  // Staff
  { pattern: /^\/staff\/?(\?.*)?$/, mapping: { storeName: 'staff', mode: 'list' } },

  // Materials (Inventory)
  { pattern: /^\/materials\/?(\?.*)?$/, mapping: { storeName: 'materials', mode: 'list' } },
  { pattern: /^\/materials\/categories\/?(\?.*)?$/, mapping: { storeName: 'inventoryCategories', mode: 'list' } },

  // Recipe Templates
  { pattern: /^\/recipe-templates\/?(\?.*)?$/, mapping: { storeName: 'recipeTemplates', mode: 'list' } },

  // Expenses
  { pattern: /^\/expenses\/?(\?.*)?$/, mapping: { storeName: 'expenses', mode: 'list' } },

  // Cash Register
  { pattern: /^\/cash-register\/my-status\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'myRegisterStatus' } },
  { pattern: /^\/cash-register\/?(\?.*)?$/, mapping: { storeName: 'cashRegisters', mode: 'list' } },

  // Shift Reports
  { pattern: /^\/shift-reports\/?(\?.*)?$/, mapping: { storeName: 'shiftReports', mode: 'list' } },

  // Profile
  { pattern: /^\/restaurants\/my\/profile\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'myProfile' } },

  // Restaurant Profile
  { pattern: /^\/restaurants\/profile\/?(\?.*)?$/, mapping: { storeName: 'restaurantProfile', mode: 'single', extractKey: () => 'myProfile' } },

  // Admin — restaurants
  { pattern: /^\/admin\/restaurants\/?(\?.*)?$/, mapping: { storeName: 'restaurants', mode: 'list' } },

  // Public menus
  { pattern: /^\/public\/menu\/([^/?]+)$/, mapping: { storeName: 'publicMenus', mode: 'single', extractKey: (url) => url.split('/').pop()?.split('?')[0] || null } },

  // Dashboard / Stats — cached as snapshots
  { pattern: /^\/dashboard\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'dashboard' } },
  { pattern: /^\/orders\/statistics\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'orderStats' } },
  { pattern: /^\/restaurants\/statistics\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'restaurantStats' } },
  { pattern: /^\/expenses\/summary\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'expenseSummary' } },
  { pattern: /^\/materials\/summary\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'inventorySummary' } },
  { pattern: /^\/profit-loss\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'profitLoss' } },
  { pattern: /^\/admin\/dashboard\/?(\?.*)?$/, mapping: { storeName: 'dashboardStats', mode: 'single', extractKey: () => 'adminDashboard' } },
];

function findCacheMapping(url: string): { mapping: CacheMapping; match: RegExpMatchArray } | null {
  const cleanUrl = url.replace(/^\/api\/v\d+/, '');
  for (const entry of CACHE_MAPPINGS) {
    const match = cleanUrl.match(entry.pattern);
    if (match) return { mapping: entry.mapping, match };
  }
  return null;
}

// ── Temp ID Generator ──

export function generateTempId(prefix: string = 'temp'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ── Build Cache Key ──

export function buildCacheKey(config: AxiosRequestConfig): string {
  const url = config.url || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.method?.toUpperCase() || 'GET'}:${url}${params ? ':' + params : ''}`;
}

// ── Entity Type Extraction ──

function extractEntityType(url: string): string {
  const cleanUrl = url.replace(/^\/api\/v\d+/, '');
  const segments = cleanUrl.split('/').filter(Boolean);
  const first = segments[0] || 'unknown';
  if (first === 'delivery-companies') {
    if (segments[1] === 'orders') {
      return 'deliveryCompanyOrder';
    }
    return 'deliveryCompany';
  }
  // Remove trailing 's' for entity type
  return first.endsWith('s') ? first.slice(0, -1) : first;
}

// ── Cache Response Interceptor (runs on SUCCESS) ──

export async function cacheResponseInterceptor(response: AxiosResponse): Promise<AxiosResponse> {
  // Only cache GET responses
  if (response.config.method?.toUpperCase() !== 'GET') return response;

  const url = response.config.url || '';
  const result = findCacheMapping(url);
  if (!result) return response;

  const { mapping } = result;

  try {
    const responseData = response.data;
    // The API typically returns { data: ... } or { data: [...], pagination: ... }
    const payload = responseData?.data ?? responseData;

    if (mapping.mode === 'list' && Array.isArray(payload)) {
      // Cache each item individually
      await db.putMany(mapping.storeName, payload);
    } else if (mapping.mode === 'single') {
      const key = mapping.extractKey?.(url);
      if (key && payload) {
        // For single items that use a non-id key, wrap with the key
        const record = payload.id ? payload : { ...payload, id: key, key, slug: key };
        await db.put(mapping.storeName, record);
      } else if (payload?.id) {
        await db.put(mapping.storeName, payload);
      }
    }

    // Also cache the full response shape for stats/dashboard
    if (mapping.storeName === 'dashboardStats') {
      const key = mapping.extractKey?.(url) || 'unknown';
      await db.put('dashboardStats', {
        key,
        data: responseData,
        cachedAt: Date.now(),
      });
    }

    await db.setLastSync(mapping.storeName);
  } catch (err) {
    console.warn('[OfflineCache] Failed to cache response:', err);
  }

  return response;
}

// ── Offline Fallback Interceptor (runs on NETWORK ERROR) ──

export async function offlineFallbackInterceptor(
  error: any
): Promise<AxiosResponse | never> {
  const config = error.config as AxiosRequestConfig | undefined;
  if (!config) throw error;

  // Only intercept network errors (no response from server)
  const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error'));
  if (!isNetworkError) throw error;

  const url = config.url || '';
  const method = (config.method?.toUpperCase() || 'GET') as string;

  // Skip offline handling for online-only routes
  if (isOnlineOnlyRoute(url)) throw error;

  // ── GET requests: serve from cache ──
  if (method === 'GET') {
    const result = findCacheMapping(url);
    if (!result) throw error;

    const { mapping } = result;

    try {
      let cachedData: any;

      if (mapping.storeName === 'dashboardStats') {
        // Return the full cached response shape
        const key = mapping.extractKey?.(url) || 'unknown';
        const cached = await db.get<any>('dashboardStats', key);
        if (cached?.data) {
          cachedData = cached.data;
        }
      } else if (mapping.mode === 'list') {
        let items = (await db.getAll(mapping.storeName)) as any[];
        
        // --- Merge Pending Operations ---
        const pendingOps = await db.getPending();
        const entityType = extractEntityType(url);
        const entityPendingOps = pendingOps.filter(op => op.entityType === entityType);
        
        // 1. DELETEs: Remove deleted items
        const deletedIds = new Set(entityPendingOps.filter(op => op.method === 'DELETE').map(op => op.entityId));
        items = items.filter(item => !deletedIds.has(item.id));
        
        // 2. Updates (PUT/PATCH): Merge changes into existing items
        const updateOps = entityPendingOps.filter(op => op.method === 'PUT' || op.method === 'PATCH');
        items = items.map(item => {
          const update = updateOps.find(op => op.entityId === item.id);
          if (update && update.data) {
            return { ...item, ...update.data, _offline: true };
          }
          return item;
        });
        
        // 3. Creates (POST): Prepend new items
        const createOps = entityPendingOps.filter(op => op.method === 'POST');
        const newItems = createOps.map(op => ({
          ...(op.data as any),
          id: op.tempId || `temp-${op.timestamp}`,
          ...(entityType === 'order' ? {
            orderNumber: op.tempId,
            status: (op.data as any)?.status || 'pending',
            totalAmount: (op.data as any)?.totalAmount || 0,
          } : {}),
          createdAt: new Date(op.timestamp).toISOString(),
          _offline: true,
        }));
        
        items = [...newItems, ...items];

        if (items.length > 0 || createOps.length > 0) {
          // Reconstruct the API response shape
          cachedData = {
            data: items,
            pagination: { total: items.length, page: 1, limit: items.length },
            _offline: true,
          };
        } else {
          // If cache is totally empty, return empty list
          cachedData = {
            data: [],
            pagination: { total: 0, page: 1, limit: 100 },
            _offline: true,
          };
        }
      } else if (mapping.mode === 'single') {
        const key = mapping.extractKey?.(url);
        if (key) {
          let item = (await db.get(mapping.storeName, key)) as any;
          
          // Apply pending updates for single item
          if (item) {
            const pendingOps = await db.getPending();
            const entityType = extractEntityType(url);
            const updateOps = pendingOps.filter(op => op.entityType === entityType && op.entityId === item.id && (op.method === 'PUT' || op.method === 'PATCH'));
            const update = updateOps[updateOps.length - 1]; // latest
            if (update && update.data) {
              item = { ...item, ...update.data, _offline: true };
            }
          }
          
          if (item) {
            cachedData = { data: item, _offline: true };
          }
        }
      }

      if (cachedData) {
        console.info(`[OfflineCache] Serving cached data for ${method} ${url}`);
        // Return a synthetic axios response
        return {
          data: cachedData,
          status: 200,
          statusText: 'OK (Offline Cache)',
          headers: {},
          config,
          request: null,
        } as AxiosResponse;
      }
    } catch (cacheErr) {
      console.warn('[OfflineCache] Failed to read from cache:', cacheErr);
    }

    // No cache available — re-throw the network error
    throw error;
  }

  // ── Mutation requests (POST/PUT/PATCH/DELETE): enqueue for sync ──
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const entityType = extractEntityType(url);
    let tempId: string | undefined;
    
    if (method === 'POST') {
      if (entityType === 'order') {
        tempId = await db.getNextOfflineOrderNumber();
      } else {
        tempId = generateTempId(entityType);
      }
    }

    // Check if the request has file data
    const isFormData = config.data instanceof FormData;
    let syncData = config.data;
    
    // Axios transforms data to JSON string before request error interceptor catches it.
    // If it's a string, we need to parse it back to an object to prevent double-stringification
    // and to allow merging pending ops locally.
    if (!isFormData && typeof config.data === 'string') {
      try {
        syncData = JSON.parse(config.data);
      } catch (e) {
        // Leave as is if it's not valid JSON
      }
    }

    let hasFiles = false;
    let fileRefs: string[] = [];

    if (isFormData) {
      // Extract files from FormData and store as blobs
      hasFiles = true;
      const formData = config.data as FormData;
      const jsonData: Record<string, any> = {};

      const entries = Array.from(formData.entries());
      for (let i = 0; i < entries.length; i++) {
        const [key, value] = entries[i];
        if (typeof value !== 'string') {
          const ref = `file-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
          await db.addFile({
            ref,
            blob: value,
            filename: value.name || `${key}.bin`,
            mimeType: value.type || 'application/octet-stream',
            fieldName: key,
            timestamp: Date.now(),
          });
          fileRefs.push(ref);
        } else {
          // Try to parse JSON values
          try {
            jsonData[key] = JSON.parse(value as string);
          } catch {
            jsonData[key] = value;
          }
        }
      }
      syncData = jsonData;
    }

    // Extract entity ID for updates
    const cleanUrl = url.replace(/^\/api\/v\d+/, '');
    const segments = cleanUrl.split('/').filter(Boolean);
    const entityId = method !== 'POST' && segments.length >= 2 ? segments[segments.length - 1] : undefined;

    const operation: Omit<SyncOperation, 'id'> = {
      timestamp: Date.now(),
      method: method as SyncOperation['method'],
      url: cleanUrl, // Store the clean URL without api prefix
      data: syncData,
      headers: config.headers
        ? Object.fromEntries(
            Object.entries(config.headers).filter(([, v]) => typeof v === 'string')
          ) as Record<string, string>
        : undefined,
      entityType,
      entityId,
      tempId,
      hasFiles,
      fileRefs: fileRefs.length > 0 ? fileRefs : undefined,
      status: 'pending',
      retryCount: 0,
    };

    const queueId = await db.enqueue(operation);

    console.info(`[OfflineCache] Queued ${method} ${url} for sync (queue id: ${queueId})`);

    // Build an optimistic response
    const optimisticData = {
      ...(syncData || {}),
      id: tempId || entityId,
      ...(entityType === 'order' ? { orderNumber: tempId } : {}),
      _offline: true,
      _tempId: tempId,
      _syncQueueId: queueId,
    };

    return {
      data: { data: optimisticData, _offline: true },
      status: 200,
      statusText: 'OK (Queued for Sync)',
      headers: {},
      config,
      request: null,
    } as AxiosResponse;
  }

  // Unknown method — re-throw
  throw error;
}
